import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  Table,
  Tabs,
  Typography,
  Button,
  notification,
  Row,
  Col,
  List
} from "antd";

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie
} from "recharts";

const { TabPane } = Tabs;
const { Title } = Typography;

const AdminDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);

  // ✅ Dashboard states (EMPTY)
  const [stats, setStats] = useState({
    totalEmployees: 0,
    present: 0,
    absent: 0,
    leave: 0
  });

  const [attendanceData, setAttendanceData] = useState([]);
  const [recruitData, setRecruitData] = useState([]);
  const [absentData, setAbsentData] = useState([]);
  const [loanData, setLoanData] = useState([]);
  const [awardData, setAwardData] = useState([]);
  const [notices, setNotices] = useState([]);
  const [newEmployees, setNewEmployees] = useState([]);
  const [employeeTable, setEmployeeTable] = useState([]);

  const token = localStorage.getItem("access-token");

  // ================= FETCH =================
  const fetchData = () => {
    axios.get("https://rhrmapi.up2dateinc.com/api/projects", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setProjects(res.data));

    axios.get("https://rhrmapi.up2dateinc.com/api/clients", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setClients(res.data));

    axios.get("https://rhrmapi.up2dateinc.com/api/tasks", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setTasks(res.data));

    axios.get("https://rhrmapi.up2dateinc.com/api/auth/pending", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setPendingUsers(res.data));

    // ✅ Fetch Latest Notices
    axios.get("https://rhrmapi.up2dateinc.com/api/notices", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setNotices(res.data));


    axios.get("https://rhrmapi.up2dateinc.com/api/auth/login-history", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setLoginHistory(res.data));

    axios.get("https://rhrmapi.up2dateinc.com/api/auth/count", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setStats(prev => ({
        ...prev,
        totalEmployees: res.data.totalEmployees
      }));
    });

    // 🔥 FUTURE DASHBOARD API
    /*
    axios.get("http://localhost:5000/api/dashboard", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setStats(res.data.stats);
      setAttendanceData(res.data.attendance);
      setRecruitData(res.data.recruit);
      setAbsentData(res.data.absent);
      setLoanData(res.data.loans);
      setAwardData(res.data.awards);
      setNotices(res.data.notices);
      setNewEmployees(res.data.newEmployees);
      setEmployeeTable(res.data.employeePoints);
    });
    */
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // ================= ACTIONS =================
  const handleApprove = (userId) => {
    axios.put(`https://rhrmapi.up2dateinc.com/api/auth/approve/${userId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(() => {
      notification.success({ message: "User Approved" });
      fetchData();
    });
  };

  const handleReject = (userId) => {
    axios.delete(`https://rhrmapi.up2dateinc.com/api/auth/reject/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(() => {
      notification.success({ message: "User Rejected" });
      fetchData();
    });
  };

  // project map
  const projectMap = {};
  projects.forEach(p => {
    projectMap[p._id] = p;
  });

  // ================= UI =================
  return (
    <div>
      <Title level={2}>Admin Dashboard</Title>

      {/* ================= TABS (UNCHANGED) ================= */}
      <Tabs defaultActiveKey="1">
        <TabPane tab="Projects" key="1">
          <Table
            dataSource={projects}
            rowKey="_id"
            columns={[
              { title: "Project Name", dataIndex: "projectName" },
              { title: "Created By", dataIndex: ["createdBy", "email"] },
              { title: "Status", dataIndex: "status" }
            ]}
          />
        </TabPane>

        <TabPane tab="Clients" key="2">
          <Table
            dataSource={clients}
            rowKey="_id"
            columns={[
              { title: "Client Name", dataIndex: "name" },
              { title: "Created By", dataIndex: ["createdBy", "email"] },
              { title: "Email", dataIndex: "email" }
            ]}
          />
        </TabPane>

        <TabPane tab="Tasks" key="3">
          <Table
            dataSource={tasks}
            rowKey="_id"
            columns={[
              {
                title: "Project Name",
                render: (_, record) =>
                  projectMap[record.projectId]?.projectName || "-"
              },
              {
                title: "Project Lead",
                render: (_, record) =>
                  projectMap[record.projectId]?.projectLead || "-"
              },
              {
                title: "Team Member",
                dataIndex: "assignee"
              },
              {
                title: "Priority",
                dataIndex: "priority",
                render: (priority) => {
                  const color =
                    priority === "High"
                      ? "red"
                      : priority === "Medium"
                      ? "orange"
                      : "green";
                  return <span style={{ color }}>{priority}</span>;
                }
              }
            ]}
          />
        </TabPane>

        <TabPane tab="Pending Users" key="4">
          <Table
            dataSource={pendingUsers}
            rowKey="_id"
            columns={[
              { title: "First Name", dataIndex: "firstName" },
              { title: "Last Name", dataIndex: "lastName" },
              { title: "Email", dataIndex: "email" },
              { title: "Username", dataIndex: "userName" },
              { title: "Status", dataIndex: "status" },
              {
                title: "Action",
                render: (_, record) => (
                  <div style={{ display: "flex", gap: "8px" }}>
                    {record.status === "pending" ? (
                      <>
                        <Button type="primary" onClick={() => handleApprove(record._id)}>
                          Approve
                        </Button>
                        <Button danger onClick={() => handleReject(record._id)}>
                          Reject
                        </Button>
                      </>
                    ) : (
                      <span>No Action</span>
                    )}
                  </div>
                )
              }
            ]}
          />
        </TabPane>

        <TabPane tab="Login" key="5">
  <Table
    dataSource={loginHistory}
    rowKey="_id"
    columns={[
      {
        title: "Username",
        dataIndex: ["userId", "userName"]
      },
      {
        title: "Email",
        dataIndex: "email"
      },
      {
        title: "Login Time",
        dataIndex: "loginTime",
        render: (time) => new Date(time).toLocaleString()
      },
      {
        title: "Logout Time",
        dataIndex: "logoutTime",
        render: (time) =>
          time ? new Date(time).toLocaleString() : "Still Logged In"
      },
      {
        title: "Current Module",
        dataIndex: "currentModule"
      },
      {
        title: "Module History",
        render: (_, record) => (
          <ul>
            {record.moduleHistory?.map((m, i) => (
              <li key={i}>
                {m.module} (
                {new Date(m.startTime).toLocaleTimeString()} -
                {m.endTime
                  ? new Date(m.endTime).toLocaleTimeString()
                  : "Active"}
                )
              </li>
            ))}
          </ul>
        )
      }
    ]}
  />
</TabPane>
      </Tabs>

      {/* ================= DASHBOARD BELOW ================= */}

      <br />

      <Row gutter={16}>
        <Col span={6}><Card title="Total Employees">{stats.totalEmployees}</Card></Col>
        <Col span={6}><Card title="Today's Present">{stats.present}</Card></Col>
        <Col span={6}><Card title="Today's Absent">{stats.absent}</Card></Col>
        <Col span={6}><Card title="Today's Leave">{stats.leave}</Card></Col>
      </Row>

      <br />

      <Row gutter={16}>
        <Col span={16}>
          <Card title="Attendance (Last 30 Days)">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={attendanceData}>
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Line dataKey="value" />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Recruited (Current Year)" style={{ marginTop: 16 }}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={recruitData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Loan Payment vs Received" style={{ marginTop: 16 }}>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={loanData} dataKey="value" label />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col span={8}>
          <Card title="Latest Notice">
            <List dataSource={notices} renderItem={(item) => <List.Item><b>{item.type}</b><br />{item.description}<br />Date: {new Date(item.date).toLocaleDateString()}</List.Item>} />
          </Card>

          <Card title="New Recruited" style={{ marginTop: 16 }}>
            <List
              dataSource={newEmployees}
              renderItem={item => (
                <List.Item>
                  {item.name} <br /> Hire Date: {item.date}
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      <br />

      <Row gutter={16}>
        <Col span={12}>
          <Card title="Absent (Last 15 Days)">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={absentData}>
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Line dataKey="value" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Awarded Current Year">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={awardData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <br />

      <Card title="Employee Points">
        <Table
          dataSource={employeeTable}
          columns={[
            { title: "Employee Name", dataIndex: "name" },
            { title: "Attendance Points", dataIndex: "attendance" },
            { title: "Collaborative Points", dataIndex: "collaboration" },
            { title: "Management Points", dataIndex: "management" },
            { title: "Total Points", dataIndex: "total" },
            { title: "Date", dataIndex: "date" }
          ]}
        />
      </Card>
    </div>
  );
};

export default AdminDashboard;